import React, { useState, useEffect } from 'react';
import { Eye, User, Calendar, SlidersHorizontal, X } from 'lucide-react';
import useEmployeeHistory from '../../../hooks/useEmployeeHistory';
import HistoryFilters from './HistoryFilters';
import HistoryEventDetails from './HistoryEventDetails';
import { useEmployees } from '../../../hooks/useEmployees';
import EventTypeBadge from './components/EventTypeBadge';
import Pagination from '../../common/Pagination';

const PAGE_SIZE = 20;

export default function HistoryModule() {
  const { employees } = useEmployees('activo');
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [showEventDetails, setShowEventDetails]  = useState(false);
  const [selectedEvent, setSelectedEvent]        = useState(null);
  const [showFilters, setShowFilters]            = useState(false);
  const [page, setPage]                          = useState(1);

  const {
    loading,
    history,
    filters,
    loadCompanyHistory,
    updateFilters,
    clearFilters
  } = useEmployeeHistory();

  useEffect(() => {
    loadCompanyHistory({ employee_id: selectedEmployee || undefined });
    setPage(1);
  }, [selectedEmployee, filters, loadCompanyHistory]);

  const handleViewEvent = (event) => {
    setSelectedEvent(event);
    setShowEventDetails(true);
  };

  const activeFiltersCount = [
    filters.categoria,
    filters.tipo_evento,
    filters.fecha_desde,
    filters.fecha_hasta,
    filters.activo !== 'true'
  ].filter(Boolean).length;

  const totalPages = Math.ceil(history.length / PAGE_SIZE);
  const pagedHistory = history.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // ── Loading ───────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Cargando historial...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* ── Header ────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
          {history.length} eventos
        </span>
        <span className="hidden sm:block text-xs text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-lg">
          Solo lectura — los eventos se registran automáticamente
        </span>
      </div>

      {/* ── Barra de búsqueda + filtros ───────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">

        {/* Selector de empleado */}
        <div className="relative">
          <User size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
          >
            <option value="">Todos los empleados</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.nombre} {emp.apellido} — {emp.employee_id}
              </option>
            ))}
          </select>
        </div>

        {/* Botón Filtros */}
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors ${
            showFilters || activeFiltersCount > 0
              ? 'border-blue-400 text-blue-600 bg-blue-50'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50'
          }`}
        >
          <SlidersHorizontal size={14} />
          Filtros
          {activeFiltersCount > 0 && (
            <span className="w-2 h-2 rounded-full bg-blue-500" />
          )}
        </button>

        {/* Stats + limpiar empleado */}
        <div className="hidden sm:flex items-center gap-2 ml-auto">
          <span className="text-xs text-gray-400">
            <span className="font-semibold text-blue-600">{history.length}</span> eventos
          </span>
          {selectedEmployee && (
            <>
              <span className="text-gray-200">|</span>
              <button
                onClick={() => setSelectedEmployee('')}
                className="text-xs text-gray-400 hover:text-gray-700 flex items-center gap-1"
              >
                <X size={11} /> Limpiar
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Filtros desplegables ──────────────────────────────────── */}
      {showFilters && (
        <HistoryFilters
          filters={filters}
          onFilterChange={updateFilters}
          onClear={clearFilters}
        />
      )}

      {/* ── Tabla ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Fecha</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Empleado</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Evento</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Descripción</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Registrado por</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Ver</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {history.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-16 text-center">
                    <Calendar size={36} className="text-gray-200 mx-auto mb-3" />
                    <p className="text-sm text-gray-500 font-medium">No hay eventos registrados</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Los eventos se crean automáticamente al gestionar contratos, acciones y documentos
                    </p>
                  </td>
                </tr>
              ) : (
                pagedHistory.map((event) => (
                  <tr key={event.id} className="hover:bg-gray-50 transition-colors">

                    {/* Fecha */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar size={13} className="text-gray-300 flex-shrink-0" />
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {new Date(event.created_at).toLocaleDateString('es-DO', {
                              day: '2-digit', month: 'short', year: 'numeric'
                            })}
                          </div>
                          <div className="text-xs text-gray-400">
                            {new Date(event.created_at).toLocaleTimeString('es-DO', {
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Empleado */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        {event.empleado_foto ? (
                          <img
                            src={event.empleado_foto}
                            alt={event.empleado_nombre}
                            className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-7 h-7 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                            <span className="text-xs font-bold text-blue-600">
                              {event.empleado_nombre?.[0]?.toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div>
                          <div className="text-sm font-medium text-gray-900">{event.empleado_nombre}</div>
                          <div className="text-xs text-gray-400">{event.empleado_codigo}</div>
                        </div>
                      </div>
                    </td>

                    {/* Evento */}
                    <td className="px-4 py-3">
                      <div className="text-sm font-semibold text-gray-900 mb-1">{event.titulo}</div>
                      <EventTypeBadge tipo_evento={event.tipo_evento} categoria={event.categoria} />
                    </td>

                    {/* Descripción */}
                    <td className="px-4 py-3 max-w-xs">
                      <p className="text-sm text-gray-500 truncate">{event.descripcion || '—'}</p>
                    </td>

                    {/* Registrado por */}
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <User size={13} className="text-gray-300 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{event.creado_por || 'Sistema'}</span>
                      </div>
                    </td>

                    {/* Ver */}
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleViewEvent(event)}
                        className="p-1.5 text-blue-500 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Ver detalles"
                      >
                        <Eye size={15} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          page={page}
          totalPages={totalPages}
          total={history.length}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      </div>

      {/* Modal detalles */}
      {showEventDetails && selectedEvent && (
        <HistoryEventDetails
          event={selectedEvent}
          onClose={() => { setShowEventDetails(false); setSelectedEvent(null); }}
        />
      )}
    </div>
  );
}
